import { EarlyEstimateInput, EarlyEstimateRow } from "_graphql-types";
import { parseNumber } from "../clientInvestments/logic";

/**
 * The API holds balances in dollars and returns/spread as fractions (0.012 = 1.2%); the grid
 * shows balances in $M and returns/spread in percent.
 */
export const ESTIMATE_FIELDS = ["grossEndBal", "netEndBal", "grossReturn", "spread"] as const;
export type EstimateField = (typeof ESTIMATE_FIELDS)[number];
export type EstimateDraft = Record<EstimateField, string>;

const isBalance = (f: EstimateField) => f === "grossEndBal" || f === "netEndBal";

/** Trim float noise (0.012 * 100 = 1.2000000000000002). */
const clean = (n: number) => Number(n.toPrecision(12));

/** An API value as the grid shows it. */
export function toDisplay(field: EstimateField, v: number | null | undefined): string {
  if (v === null || v === undefined) return "";
  return String(clean(isBalance(field) ? v / 1e6 : v * 100));
}

/** What the grid shows back as the API value: dollars to the cent, fractions. */
export function fromDisplay(field: EstimateField, text: string, label: string): number | null {
  const v = parseNumber(text, label);
  if (v === null) return null;
  return isBalance(field) ? Math.round(v * 1e6 * 100) / 100 : clean(v / 100);
}

type Overrides = Pick<EarlyEstimateRow, "rcgFundId" | "name" | "isEstimate" | "version" | EstimateField>;

export const estimateInitial = (row: Overrides): EstimateDraft => ({
  grossEndBal: toDisplay("grossEndBal", row.grossEndBal),
  netEndBal: toDisplay("netEndBal", row.netEndBal),
  grossReturn: toDisplay("grossReturn", row.grossReturn),
  spread: toDisplay("spread", row.spread),
});

const LABEL: Record<EstimateField, string> = {
  grossEndBal: "Gross end bal",
  netEndBal: "Net end bal",
  grossReturn: "Gross return",
  spread: "Spread",
};

/**
 * The saveEarlyEstimates input: one entry per fund whose overrides changed, carrying all four
 * (the API writes all four, so an omitted one would be lost; null clears one, and a null spread
 * saves the system spread).
 * Reconciled funds (isEstimate false) are never sent.
 */
export function estimateInputs(rows: Overrides[], drafts: ReadonlyMap<number, EstimateDraft>): EarlyEstimateInput[] {
  return rows.flatMap(row => {
    const draft = drafts.get(row.rcgFundId);
    if (!draft || !row.isEstimate) return [];
    const initial = estimateInitial(row);
    if (ESTIMATE_FIELDS.every(f => draft[f].trim() === initial[f])) return [];
    // An untouched field goes back exactly as it came, not through the display rounding.
    const value = (f: EstimateField) =>
      draft[f].trim() === initial[f] ? (row[f] ?? null) : fromDisplay(f, draft[f], `${row.name} ${LABEL[f]}`);
    return [
      {
        rcgFundId: row.rcgFundId,
        grossEndBal: value("grossEndBal"),
        netEndBal: value("netEndBal"),
        grossReturn: value("grossReturn"),
        spread: value("spread"),
        // The version this edit started from: the server refuses it if someone saved since.
        expectedVersion: row.version ?? null,
      },
    ];
  });
}

// ---- Spreadsheet upload of gross returns ---------------------------------------------------------

/** The upload's columns, as the template has them. Matched ignoring case, spaces and punctuation. */
export const UPLOAD_COLUMNS = { fundId: "RCG Fund ID", fundName: "RCG Fund", grossReturn: "Gross Return %" } as const;

/** A monthly gross return past this (in percent) is a typo or a fraction-for-percent mix-up. */
export const MAX_ABS_GROSS_RETURN_PCT = 100;

const headerKey = (cell: unknown) => String(cell ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const FUND_ID_HEADERS = new Set(["rcgfundid", "fundid"]);
const GROSS_RETURN_HEADERS = new Set(["grossreturn", "grossreturnpct", "grossreturnpercent"]);

export interface ReturnsUpload {
  /** Fund id -> gross return as the grid shows it (percent text). Empty when there are errors. */
  returns: Map<number, string>;
  /** One line per problem, naming the spreadsheet row. */
  errors: string[];
}

/**
 * The gross returns in an uploaded sheet (first row the headers, cells as their displayed text).
 * All or nothing: any bad row and no returns are taken, so a half-applied file can't be saved.
 * A blank return leaves its fund unchanged; other columns (the template's fund name) are ignored.
 */
export function parseReturnsSheet(table: readonly (readonly unknown[])[], funds: readonly Overrides[]): ReturnsUpload {
  const [header = [], ...body] = table;
  const idCol = header.findIndex(c => FUND_ID_HEADERS.has(headerKey(c)));
  const returnCol = header.findIndex(c => GROSS_RETURN_HEADERS.has(headerKey(c)));
  if (idCol < 0 || returnCol < 0) {
    return {
      returns: new Map(),
      errors: [`The first row must have the column headers "${UPLOAD_COLUMNS.fundId}" and "${UPLOAD_COLUMNS.grossReturn}" (download the template)`],
    };
  }
  const byId = new Map(funds.map(f => [f.rcgFundId, f]));
  const returns = new Map<number, string>();
  const seen = new Map<number, number>();
  const errors: string[] = [];
  body.forEach((cells, i) => {
    const line = i + 2; // the spreadsheet's own row number: 1 is the header
    const idText = String(cells[idCol] ?? "").trim().replace(/,/g, "");
    const returnText = String(cells[returnCol] ?? "").trim().replace(/,/g, "").replace(/%$/, "").trim();
    if (idText === "" && returnText === "") return;
    if (!/^\d+$/.test(idText)) {
      errors.push(`Row ${line}: "${idText}" is not an ${UPLOAD_COLUMNS.fundId}`);
      return;
    }
    const id = Number(idText);
    const fund = byId.get(id);
    if (!fund) {
      errors.push(`Row ${line}: fund ${id} is not in this month's funds`);
      return;
    }
    const first = seen.get(id);
    if (first !== undefined) {
      errors.push(`Row ${line}: fund ${id} (${fund.name}) is also on row ${first}`);
      return;
    }
    seen.set(id, line);
    if (returnText === "") return;
    const pct = Number(returnText);
    if (!Number.isFinite(pct)) {
      errors.push(`Row ${line}: "${returnText}" is not a number (${fund.name})`);
      return;
    }
    if (Math.abs(pct) > MAX_ABS_GROSS_RETURN_PCT) {
      errors.push(`Row ${line}: ${pct}% is outside ±${MAX_ABS_GROSS_RETURN_PCT}%; enter percent, e.g. 1.25 for 1.25% (${fund.name})`);
      return;
    }
    if (!fund.isEstimate) {
      errors.push(`Row ${line}: ${fund.name} is reconciled for this month; its estimate can't be changed`);
      return;
    }
    returns.set(id, returnText);
  });
  return errors.length ? { returns: new Map(), errors } : { returns, errors };
}

/** The template's rows: every fund still open this month, with its current gross return override. */
export const templateRows = (funds: readonly Overrides[]) => [
  [UPLOAD_COLUMNS.fundId, UPLOAD_COLUMNS.fundName, UPLOAD_COLUMNS.grossReturn],
  ...funds
    .filter(f => f.isEstimate)
    .map(f => [f.rcgFundId, f.name, f.grossReturn === null || f.grossReturn === undefined ? "" : Number(toDisplay("grossReturn", f.grossReturn))]),
];
